import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { showTemple as getTemplePermission } from '@fs/zion-permissions'
import listService from '../../lib/listService'
import utils from '../../lib/utils'

export const LabelContext = createContext()

const systemLabels = {
  [utils.FILTER_ACTIVELY_CHANGING]: i18n.t('choice-chip.actively-changing', 'Actively Changing'),
  [utils.FILTER_COMPLETED]: i18n.t('choice-chip.completed', 'Completed'),
  [utils.FILTER_END_OF_LINE]: i18n.t('choice-chip.end-of-line', 'End of Line'),
  [utils.FILTER_INTERESTED_IN]: i18n.t('choice-chip.interested-in', 'Interested In'),
  [utils.FILTER_RESEARCHING]: i18n.t('choice-chip.researching', 'Researching'),
  [utils.FILTER_STUCK]: i18n.t('choice-chip.stuck', 'Stuck'),
}

const labelDataTestIds = {
  [utils.FILTER_ACTIVELY_CHANGING]: 'filter-actively-changing',
  [utils.FILTER_COMPLETED]: 'filter-completed',
  [utils.FILTER_END_OF_LINE]: 'filter-end-of-line',
  [utils.FILTER_INTERESTED_IN]: 'filter-interested-in',
  [utils.FILTER_RESEARCHING]: 'filter-researching',
  [utils.FILTER_STUCK]: 'filter-stuck',
  [utils.FILTER_TEMPLE_RESERVATION]: 'filter-temple-reservation',
  [utils.FILTER_TEMPLE_YEAR_RULE]: 'filter-temple-year-rule',
  [utils.FILTER_CUSTOM]: 'filter-custom',
}

/**
 * @description - Used for add/edit/remove label overlay (LabelOverlay)
 * @param props
 */
export default function LabelProvider(props) {
  const { children } = props
  const focusBackRef = useRef()
  const labelOverlay = useOverlay({ focusBackRef })
  const changeFocusBackRef = (newFocusBackEl) => {
    focusBackRef.current = newFocusBackEl
  }
  const [customLabelTitle, setCustomLabelTitle] = useState('')
  const [filterChoices, setFilterChoices] = useState([]) // Used for OptionsDrawer to display all filterable labels (labels that are actually used)
  const [labelInfo, setLabelInfo] = useState({})
  const [labelNotes, setLabelNotes] = useState('')
  const [mode, setMode] = useState('add') // Possible values: add, edit, view, create-label, edit-label, delete-label
  const [originalLabelId, setOriginalLabelId] = useState('') // Used when editing a label's label type - the original label the user opened the overlay for
  const [personObj, setPersonObj] = useState({})
  const [prevMode, setPrevMode] = useState(null) // Which mode to return to after adding or deleting a custom label
  const [selectedLabelType, setSelectedLabelType] = useState('') // The type of label that is selected for adding or opened for viewing/editing
  const [showTemple, setShowTemple] = useState(false)
  const [usedLabels, setUsedLabels] = useState([])
  const [updatedPersonLabels, setUpdatedPersonLabels] = useState({})

  useEffect(() => {
    let isCurrent = true
    getTemplePermission()
      .then((permission) => {
        if (isCurrent) setShowTemple(permission)
      })
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [])

  // Update systemLabels when locale changes
  useEffect(() => {
    Object.assign(systemLabels, {
      [utils.FILTER_ACTIVELY_CHANGING]: i18n.t('choice-chip.actively-changing', 'Actively Changing'),
      [utils.FILTER_COMPLETED]: i18n.t('choice-chip.completed', 'Completed'),
      [utils.FILTER_END_OF_LINE]: i18n.t('choice-chip.end-of-line', 'End of Line'),
      [utils.FILTER_INTERESTED_IN]: i18n.t('choice-chip.interested-in', 'Interested In'),
      [utils.FILTER_RESEARCHING]: i18n.t('choice-chip.researching', 'Researching'),
      [utils.FILTER_STUCK]: i18n.t('choice-chip.stuck', 'Stuck'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language])

  useEffect(() => {
    if (showTemple) {
      systemLabels[utils.FILTER_TEMPLE_RESERVATION] = i18n.t('choice-chip.temple-reservation', 'Temple Reservation')
      systemLabels[utils.FILTER_TEMPLE_YEAR_RULE] = i18n.t('choice-chip.temple-year-rule', 'Temple 110-Year Rule')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTemple, i18n.language])

  useEffect(() => {
    updateFilterableLabels(showTemple, setFilterChoices)
  }, [showTemple])

  useEffect(() => {
    let isCurrent = true
    // Load up the current list of Labels with their labelIds for system and custom Labels
    listService
      .getUserLabels(utils.LABEL_SHOW_ALL)
      .then((data) => {
        if (isCurrent) {
          const info =
            data?.labels?.reduce((acc, label, index) => {
              acc[label.title] = label
              // To maintain the order of when the labels were created, we'll store the index of the array to help us sort in LabelSelect.js
              acc[label.title].listPosition = index
              if (label.system) acc[label.title].title = systemLabels[label.title]
              return acc
            }, {}) ?? {}

          setLabelInfo(info)
        }
      })
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [setLabelInfo, showTemple, personObj])

  // Updates labels in OptionDrawers and in PersonAttachedLabels when user adds/creates/removes/deletes a label
  const updateOptionsLabelFilters = useCallback(() => {
    updateFilterableLabels(showTemple, setFilterChoices)
  }, [showTemple])

  const values = {
    changeFocusBackRef,
    customLabelTitle,
    filterChoices,
    labelDataTestIds,
    labelInfo,
    labelNotes,
    labelOverlay,
    mode,
    originalLabelId,
    personObj,
    prevMode,
    selectedLabelType,
    setCustomLabelTitle,
    setLabelInfo,
    setLabelNotes,
    setMode,
    setOriginalLabelId,
    setPersonObj,
    setPrevMode,
    setSelectedLabelType,
    setUpdatedPersonLabels,
    setUsedLabels,
    showTemple,
    systemLabels,
    updatedPersonLabels,
    updateOptionsLabelFilters,
    usedLabels,
  }

  return <LabelContext.Provider value={values}>{children}</LabelContext.Provider>
}

function updateFilterableLabels(showTemple, setFilterChoices) {
  listService
    .getUserLabels(utils.LABEL_SHOW_FILTERABLE)
    .then((data) => {
      if (!data?.labels?.length) setFilterChoices([])
      else {
        const choices = [
          {
            label: i18n.t('choice-chip.all', 'All'),
            value: utils.FILTER_ALL,
            id: null,
            'data-testid': `options-filter-all`,
          },
        ]
        data?.labels?.forEach((label) => {
          if (
            showTemple ||
            (label.title !== utils.FILTER_TEMPLE_RESERVATION && label.title !== utils.FILTER_TEMPLE_YEAR_RULE)
          ) {
            choices.push({
              label: label.system ? systemLabels[label.title] : label.title,
              value: label.title,
              id: label.labelId,
              'data-testid': `options-${labelDataTestIds[label.title]}`,
            })
          }
        })
        setFilterChoices(choices)
      }
    })
    .catch(console.error)
}
